import useSources from "~/ui-rtk/hooks/sources"
import { useStats } from "../hooks/stats"
import {
  AD_MEDIA_CATEGORIES_OPTIONS,
  TAdMediaOption,
  TCampaignAdSetAdDetailsProps,
} from "../constants"
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAppDispatch } from "~/ui-rtk/app/hooks"
import { setIsOpen as setDrawerIsOpen } from "~/ui-rtk/app/slices/drawer.slice"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import { useAdsCubeQuery } from "./hooks/cube-query"
import { H5, H6 } from "~/ui-rtk/components/ui/typography"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import {
  MarathonImage,
  mapMediaChannelToSourceType,
} from "~/ui-rtk/components/atoms"
import { renderSourceIcon } from "~/ui-rtk/components/ui/charts/TableVisualization/components/Cell/utils"
import { TSource } from "~/ui-rtk/constants/table-visualization-types"
import BrandValueOverSpendProgressBar from "../components/BrandValueOverSpendProgressBar"
import { ArrowTopRightOnSquare } from "~/ui-rtk/components/ui/svg/arrow/ArrowTopRightOnSquare"
import { Select } from "~/ui-rtk/components/ui/controls"
import StatsTable from "../components/StatsTable"
import { renderDetailsCaption } from "../utils"
import { DetailsTitle } from "../components/DetailsHeader"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  useMediaCategoryControllerGetMediaCategoryQuery,
  useMediaCategoryControllerPostMediaCategoriesMutation,
} from "~/ui-rtk/api/mediaCategoryApi"
import ListBox from "~/ui-rtk/components/ui/common/ListBox/ListBox"

import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import useAccess from "~/ui-rtk/hooks/access"
import useAdForgeAccess from "~/ui-rtk/hooks/ad-forge-access"
import { TagPanel } from "~/ui-rtk/components/ui/charts/TableVisualization/components/TagPanel"
import type { TagPill } from "~/ui-rtk/components/ui/charts/TableVisualization/components/TagPanel/TagPillGroup"
import { useTagCategories } from "~/ui-rtk/hooks/use-structured-tags"
import { formatTagLabel } from "~/ui-rtk/types/structured-tags"
import useMediaTagAccess from "~/ui-rtk/hooks/media-tag-access"
import { useBulkGetTagAssignmentsQuery } from "~/ui-rtk/api/tagAssignmentsApi"
import {
  paidMediaApi,
  toCreativeAnalysisPlatform,
  useAnalyzeAdCreativeMutation,
  useGetAdCreativeAnalysisQuery,
  type AdCreativeAnalysisResult,
  type AnalyzeCreativeJobResult,
} from "~/ui-rtk/api/paidMediaApi"
import { isAbortError, useWaitForJob } from "~/ui-rtk/hooks/useWaitForJob"
import { useResolveAssetsMutation } from "~/ui-rtk/api/assetsApi"
import CreativeAnalysisPanel from "../components/CreativeAnalysisPanel"
import { toast } from "react-toastify"

export enum COMPARE_AD_MODE {
  ALL,
  CURRENT_AD_SET,
  CURRENT_CAMPAIGN,
}
export type TDataKey = "ALL" | "CURRENT_CAMPAIGN" | "CURRENT_AD_SET"
export const COMPARE_AD_KEY_MAP: Record<COMPARE_AD_MODE, TDataKey> = {
  [COMPARE_AD_MODE.ALL]: "ALL",
  [COMPARE_AD_MODE.CURRENT_AD_SET]: "CURRENT_AD_SET",
  [COMPARE_AD_MODE.CURRENT_CAMPAIGN]: "CURRENT_CAMPAIGN",
}
export const COMPARE_AD_LABEL_MAP: Record<COMPARE_AD_MODE, string> = {
  [COMPARE_AD_MODE.ALL]: "All",
  [COMPARE_AD_MODE.CURRENT_AD_SET]: "Current Ad Set",
  [COMPARE_AD_MODE.CURRENT_CAMPAIGN]: "Current Campaign",
}

export default function AdDetails(props: TCampaignAdSetAdDetailsProps) {
  const { sources } = useSources()
  const { metric, widget } = props
  const stats = useStats(metric)

  const { isEnabled: isDemoEnabled } = useDemoMode()
  const { hasAccess: isAdForgeEnabled } = useAdForgeAccess()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [category, setCategory] = useState<string | null | undefined>(undefined)

  const campaignName = metric.campaign_name as string
  const campaignId = metric.campaign_id as string
  const adSetName = metric.adset_name as string
  const adSetId = metric.adset_id as string
  const adName = metric.ad_name as string
  const adId = metric.ad_id as string
  const sourceName = (metric.media_channel as string)?.toUpperCase()
  const source = sources.get(sourceName)
  // Which analyze-creative platform this ad's channel maps to; null (e.g.
  // google_ads search) hides the AI analysis entirely.
  const analysisPlatform = toCreativeAnalysisPlatform(
    metric.media_channel as string,
  )

  const cachedImage = metric.md_image_url

  const isMediaTagsEnabled = useMediaTagAccess()
  const { categories: tagCategories } = useTagCategories()
  const { data: adTagAssignments, isLoading: isLoadingAdTags } =
    useBulkGetTagAssignmentsQuery(
      { itemType: "ad", itemIds: [adId] },
      { skip: !isMediaTagsEnabled || !adId },
    )

  const tagPills = useMemo<TagPill[]>(() => {
    const itemAssignments = adTagAssignments?.[adId] ?? {}
    // Resolve category slug → display label (may be a company alias).
    const categoryLabelBySlug = new Map(
      tagCategories.map(c => [c.slug, c.label]),
    )
    const pills: TagPill[] = []
    for (const [slug, assignments] of Object.entries(itemAssignments)) {
      for (const assignment of assignments) {
        pills.push({
          label: assignment.optionLabel,
          color: assignment.optionColor,
          categoryLabel: categoryLabelBySlug.get(slug) ?? formatTagLabel(slug),
        })
      }
    }
    return pills
  }, [adTagAssignments, adId, tagCategories])

  const [compareAdSetMode, setCompareAdSetMode] = useState(
    COMPARE_AD_KEY_MAP[COMPARE_AD_MODE.ALL],
  )

  // Analysis runs as an `analyze-creative` job (videos take ~30-90s, past the
  // 29s API Gateway cutoff that used to surface as a CORS error): enqueue,
  // then poll until it lands. The result is kept in local state rather than
  // the mutation's, since the mutation now only returns the job id.
  const [analyzeAdCreative] = useAnalyzeAdCreativeMutation()
  const waitForJob = useWaitForJob()
  const [creativeAnalysis, setCreativeAnalysis] =
    useState<AdCreativeAnalysisResult | null>(null)
  const [isAnalyzingCreative, setIsAnalyzingCreative] = useState(false)
  // Aborts the in-flight job wait when the drawer moves to another ad or
  // unmounts — the job itself keeps running server-side (and saves).
  const analysisAbortRef = useRef<AbortController | null>(null)
  useEffect(() => () => analysisAbortRef.current?.abort(), [])

  // A previously saved analysis (Asset.metadata.creativeAnalysis) shows on
  // open; a fresh analyze result from this session takes precedence over it.
  const { data: savedAnalysisData } = useGetAdCreativeAnalysisQuery(
    { platform: analysisPlatform ?? "meta", adId },
    { skip: !analysisPlatform || !adId },
  )
  const displayedAnalysis:
    | (AdCreativeAnalysisResult & { analyzedAt?: string | null })
    | null = creativeAnalysis ?? savedAnalysisData?.savedAnalysis ?? null

  // Debug aid (super admins only): surface the Asset-library id this ad's
  // media resolves to (AssetMapping sourceType:externalRefId), so the Asset
  // row (which holds the saved creativeAnalysis metadata + embedding) can be
  // cross-checked straight from the drawer. Regular users never fire the
  // extra resolve call — MarathonImage does its own for the preview.
  const { isSuperAdmin } = useAccess()
  const [resolveAssets] = useResolveAssetsMutation()
  const [debugAssetId, setDebugAssetId] = useState<string | null>(null)
  const debugSourceType = mapMediaChannelToSourceType(
    metric.media_channel as string,
  )
  useEffect(() => {
    setDebugAssetId(null)
    if (!isSuperAdmin || !adId || !debugSourceType) {
      return
    }
    resolveAssets({
      assets: [{ sourceType: debugSourceType, externalRefId: adId }],
    })
      .unwrap()
      .then(result => {
        setDebugAssetId(
          result.results[`${debugSourceType}:${adId}`]?.id ?? null,
        )
      })
      .catch(() => setDebugAssetId(null))
  }, [isSuperAdmin, adId, debugSourceType, resolveAssets])

  const [isAnalysisSaved, setIsAnalysisSaved] = useState(false)

  // The drawer is a persistent instance — browsing to another ad changes the
  // adId prop without remounting. Clear the previous ad's analysis so its
  // report can't be shown against the new ad, and stop waiting on its job.
  const analyzedAdIdRef = useRef(adId)
  useEffect(() => {
    if (analyzedAdIdRef.current === adId) return
    analyzedAdIdRef.current = adId
    analysisAbortRef.current?.abort()
    analysisAbortRef.current = null
    setCreativeAnalysis(null)
    setIsAnalyzingCreative(false)
    setIsAnalysisSaved(false)
  }, [adId])

  const handleAnalyzeCreative = async () => {
    if (!analysisPlatform || isAnalyzingCreative) {
      return
    }
    analysisAbortRef.current?.abort()
    const abort = new AbortController()
    analysisAbortRef.current = abort
    setIsAnalysisSaved(false)
    setIsAnalyzingCreative(true)
    try {
      // `save: true` — the job persists the report right away (no Save
      // button); tags stay suggestions until applied individually below.
      const { jobRunId } = await analyzeAdCreative({
        platform: analysisPlatform,
        adId,
        save: true,
      }).unwrap()
      const job = await waitForJob(jobRunId, { signal: abort.signal })
      if (job.status !== "COMPLETED") {
        throw new Error(
          job.error ??
            `Creative analysis ${job.status.toLowerCase()} before it finished`,
        )
      }
      const row = (job.result as AnalyzeCreativeJobResult | null)?.results[0]
      if (!row || row.error || !row.analysis || !row.mediaType) {
        throw new Error(row?.error ?? "Failed to analyze the ad creative")
      }
      setCreativeAnalysis({
        adId: row.adId,
        adName: row.adName ?? adName,
        mediaType: row.mediaType,
        analysis: row.analysis,
        suggestedTags: row.suggestedTags ?? [],
        suggestedMediaCategory: row.suggestedMediaCategory,
      })
      if (row.saved) {
        setIsAnalysisSaved(true)
        // The saved-analysis probe is cached per ad; the job wrote a new one.
        dispatch(
          paidMediaApi.util.invalidateTags([
            { type: "AdCreativeAnalysis", id: adId },
          ]),
        )
      } else {
        toast.error(row.saveError ?? "Failed to save the analysis")
      }
    } catch (err) {
      // Aborted = the user moved to another ad; the adId effect already reset.
      if (isAbortError(err)) return
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        (err instanceof Error ? err.message : undefined)
      toast.error(message ?? "Failed to analyze the ad creative")
    } finally {
      if (analysisAbortRef.current === abort) {
        analysisAbortRef.current = null
        setIsAnalyzingCreative(false)
      }
    }
  }

  const [createMediaCategory] =
    useMediaCategoryControllerPostMediaCategoriesMutation()

  const {
    data: mediaCategory,
    isLoading: mediaCategoryLoading,
    isFetching: mediaCategoryFetching,
    refetch,
  } = useMediaCategoryControllerGetMediaCategoryQuery(
    {
      id: adId,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    if (mediaCategory?.category) {
      setCategory(mediaCategory.category)
    } else {
      setCategory(null)
    }
  }, [mediaCategory])

  const categoryUpdate = async (value: TAdMediaOption) => {
    await createMediaCategory({
      body: {
        mediaId: adId,
        type: "ad",
        channel: metric.media_channel as string,
        category: value,
      },
    })
    setCategory(value)
    await refetch()
  }

  const suggestedMediaCategory = AD_MEDIA_CATEGORIES_OPTIONS.find(
    option => option === displayedAnalysis?.suggestedMediaCategory,
  )

  const { allAdsDetails, isLoading } = useAdsCubeQuery(
    compareAdSetMode === COMPARE_AD_KEY_MAP[COMPARE_AD_MODE.CURRENT_AD_SET]
      ? adSetId
      : undefined,
    compareAdSetMode === COMPARE_AD_KEY_MAP[COMPARE_AD_MODE.CURRENT_CAMPAIGN]
      ? campaignId
      : undefined,
  )

  const compareAdSetModeOptions = useMemo(
    () => [
      {
        value: COMPARE_AD_KEY_MAP[COMPARE_AD_MODE.ALL],
        label: COMPARE_AD_LABEL_MAP[COMPARE_AD_MODE.ALL],
      },
      {
        value: COMPARE_AD_KEY_MAP[COMPARE_AD_MODE.CURRENT_CAMPAIGN],
        label: COMPARE_AD_LABEL_MAP[COMPARE_AD_MODE.CURRENT_CAMPAIGN],
      },
      {
        value: COMPARE_AD_KEY_MAP[COMPARE_AD_MODE.CURRENT_AD_SET],
        label: COMPARE_AD_LABEL_MAP[COMPARE_AD_MODE.CURRENT_AD_SET],
      },
    ],
    [],
  )

  const withinCurrent = useMemo(() => {
    if (
      compareAdSetMode === COMPARE_AD_KEY_MAP[COMPARE_AD_MODE.CURRENT_AD_SET]
    ) {
      return "ad set"
    }
    if (
      compareAdSetMode === COMPARE_AD_KEY_MAP[COMPARE_AD_MODE.CURRENT_CAMPAIGN]
    ) {
      return "campaign"
    }
  }, [compareAdSetMode])

  const isViewAdLinkDisabled = useMemo(() => {
    switch (widget) {
      case "campaign":
        return metric.campaign_status === "PAUSED"
      case "adset":
        return metric.adset_status === "PAUSED"
      case "ad":
      default:
        return metric.ad_status === "PAUSED"
    }
  }, [metric, widget])

  const renderViewAdPreviewLink = () => {
    const children = (
      <>
        <ArrowTopRightOnSquare fill="white" size={20} /> View Ad Preview
      </>
    )
    const className =
      "gap-2 justify-center items-center p-4 w-full blue bg-basic-dark-blue border border-basic-blue flex rounded-lg text-4 line-height-4 font-semibold leading-5"
    if (isViewAdLinkDisabled) {
      return (
        <div className={cn(className, "opacity-50 cursor-default")}>
          {children}
        </div>
      )
    }
    return (
      <Link
        to={metric.ad_link as string}
        target="_blank"
        className={cn(
          className,
          "cursor-pointer transition-all hover:bg-basic-blue active:scale-95",
        )}
      >
        {children}
      </Link>
    )
  }

  return (
    <div className="campaign-details w-full h-full">
      <DetailsTitle
        title={adName ?? ""}
        classes={{ container: "mb-4" }}
        demoMode={isDemoEnabled}
      />
      {isLoading && (
        <div className="h-[50%] w-full flex items-center justify-center">
          <Loader />
        </div>
      )}
      {!isLoading && (
        <>
          <div className="col-span-7">
            {mediaCategoryLoading || mediaCategoryFetching ? (
              <Loader />
            ) : (
              <ListBox
                options={AD_MEDIA_CATEGORIES_OPTIONS}
                onSelect={categoryUpdate as (value: string) => void}
                value={category ?? undefined}
                label="Category"
                placeholder="Please select a Category"
              />
            )}
          </div>
          {isMediaTagsEnabled && (
            <Card
              rounded
              className="mt-3 bg-basic-dark-blue border border-basic-blue"
            >
              <H6 className="font-semibold mb-2">Tags</H6>
              <TagPanel
                itemId={adId}
                itemType="ad"
                preloadedPills={tagPills}
                categories={tagCategories}
                adName={adName}
                adSetName={adSetName}
                campaignName={campaignName}
                platform={sourceName?.toLowerCase()}
                isLoadingPills={isLoadingAdTags}
              />
            </Card>
          )}
          <div className="flex justify-between items-center mt-3">
            Compare against:
            <Select
              className="w-[50%] bg-basic-dark-blue"
              options={compareAdSetModeOptions}
              value={compareAdSetMode}
              onChange={($evt: ChangeEvent<HTMLSelectElement>) => {
                setCompareAdSetMode(
                  ($evt.target as HTMLSelectElement)
                    .value as (typeof COMPARE_AD_KEY_MAP)[keyof typeof COMPARE_AD_KEY_MAP],
                )
              }}
            />
          </div>
          <Card
            rounded
            className="bg-background-dark border border-basic-grey-inactive p-5 mt-3"
          >
            <MarathonImage
              sourceType={mapMediaChannelToSourceType(
                metric.media_channel as string,
              )}
              externalRefId={adId}
              url={cachedImage as string}
              useThumbnail={false}
              alt={adName}
              className={cn("w-full h-52")}
            />
            {isSuperAdmin && (
              <div
                className="mt-2 text-xs font-mono text-basic-grey-inactive cursor-pointer select-all"
                title="Asset-library id (click to copy)"
                onClick={() => {
                  if (debugAssetId) {
                    navigator.clipboard.writeText(debugAssetId)
                    toast.success("Asset id copied")
                  }
                }}
              >
                Asset: {debugAssetId ?? "—"}
              </div>
            )}
          </Card>
          <Card
            rounded
            className="mt-3 bg-basic-dark-blue border border-basic-blue"
          >
            <div className="grid grid-cols-8">
              <div className="col-span-7">
                <H5
                  className={cn("font-bold mb-2", isDemoEnabled && "blur-md")}
                >
                  {adName as React.ReactNode}
                </H5>
                <H6 className={cn("font-semibold", isDemoEnabled && "blur-md")}>
                  Ad Set: {adSetName as React.ReactNode}
                </H6>
                <H6 className={cn("font-semibold", isDemoEnabled && "blur-md")}>
                  Campaign: {campaignName as React.ReactNode}
                </H6>
              </div>
              <div className="flex items-center justify-end">
                {renderSourceIcon({
                  name: source?.name,
                  icon: source?.icon,
                } as TSource)}
              </div>
            </div>
            <div className="mt-3 text-sm">
              {renderDetailsCaption(
                metric,
                (allAdsDetails as Record<string, unknown>) ??
                  ({} as Record<string, unknown>),
                "ad",
                sourceName.toLowerCase() as "meta" | "tiktok",
              )}
            </div>
            <BrandValueOverSpendProgressBar
              metric={metric}
              allDetails={
                (allAdsDetails as Record<string, unknown>) ??
                ({} as Record<string, unknown>)
              }
              label="ad"
              withinCurrent={withinCurrent}
            />
          </Card>
          <div className="mt-3 flex gap-3">
            {!!metric.ad_link && (
              <div className={cn("flex-1", !isAdForgeEnabled && "flex-grow")}>
                {renderViewAdPreviewLink()}
              </div>
            )}
            {isAdForgeEnabled && (
              <button
                onClick={() => {
                  // Deep-link into AdBlaster's Ads tab with this ad
                  // pre-selected (consumes `?adId=`). AdBlaster owns the
                  // blast flow now — the previous AdForge round-trip is gone.
                  dispatch(setDrawerIsOpen(false))
                  navigate(
                    `${brandRoutes.AD_BLASTER.ADS}?adId=${encodeURIComponent(adId)}`,
                  )
                }}
                className="gap-2 flex-1 justify-center items-center p-4 bg-brand border border-brand-dark flex rounded-lg text-4 line-height-4 font-semibold leading-5 text-white cursor-pointer transition-all hover:bg-basic-pink-hover active:scale-95"
              >
                Blast Ad
              </button>
            )}
          </div>
          {analysisPlatform && (
            <CreativeAnalysisPanel
              itemType="ad"
              itemId={adId}
              analysis={displayedAnalysis}
              isAnalyzing={isAnalyzingCreative}
              isSaved={isAnalysisSaved}
              onAnalyze={() => void handleAnalyzeCreative()}
              textAnalysisTitle="Ad Metadata Analysis"
              mediaCategory={{
                suggested: suggestedMediaCategory,
                current: category,
                apply: async () => {
                  if (suggestedMediaCategory) {
                    await categoryUpdate(suggestedMediaCategory)
                  }
                },
              }}
            />
          )}
          <Card
            rounded
            className="mt-3 bg-basic-dark-blue border border-basic-blue"
          >
            <H5 className="font-bold">Stats</H5>
            <StatsTable {...stats} />
          </Card>
        </>
      )}
    </div>
  )
}
